import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Phone, 
  Mail, 
  MapPin,
  Send,
  CheckCircle,
  Clock
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    clientType: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, clientType: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        subject: formData.subject || null,
        message: formData.message,
        client_type: formData.clientType || null
      });
      
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        clientType: '',
        subject: '',
        message: ''
      });
    } catch (err) {
      setError('Something went wrong. Please try again or call me directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-amber-700 font-medium tracking-wide uppercase text-sm mb-4">Contact</p>
          <h1 
            className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-slate-900 mb-6 max-w-3xl mx-auto"
            data-testid="contact-title"
          >
            Let's Talk <span className="text-amber-700">Title</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
            No pressure, no obligation. Just a conversation about how I can help with your 
            upcoming transaction. Reach out however you're most comfortable.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-semibold text-slate-900 mb-8">
                Get In Touch
              </h2>
              
              <div className="space-y-6 mb-12">
                <a 
                  href="tel:+12154166104"
                  className="flex items-start gap-4 group"
                  data-testid="contact-phone"
                >
                  <div className="w-12 h-12 bg-amber-700/10 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-amber-700 transition-colors">
                    <Phone className="w-5 h-5 text-amber-700 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Phone</p>
                    <p className="text-slate-600">(215) 416-6104</p>
                    <p className="text-sm text-slate-500">Best way to reach me quickly</p>
                  </div>
                </a>

                <a 
                  href="mailto:Terry@alegaltitle.com"
                  className="flex items-start gap-4 group"
                  data-testid="contact-email"
                >
                  <div className="w-12 h-12 bg-amber-700/10 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-amber-700 transition-colors">
                    <Mail className="w-5 h-5 text-amber-700 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Email</p>
                    <p className="text-slate-600">Terry@alegaltitle.com</p>
                    <p className="text-sm text-slate-500">I respond within 24 hours</p>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-700/10 rounded-xl flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Service Area</p>
                    <p className="text-slate-600">PA, NJ, FL & NY</p>
                    <p className="text-sm text-slate-500">Serving Multiple States</p>
                  </div>
                </div>
              </div>

              {/* Response Time */}
              <div className="bg-slate-50 rounded-2xl p-6" data-testid="response-info">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-700/10 rounded-xl flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Quick Response Promise</p>
                    <p className="text-sm text-slate-600">
                      I typically respond within a few hours during business days. 
                      For urgent matters, call me directly – I actually answer the phone.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-slate-50 rounded-2xl p-8">
                {isSubmitted ? (
                  <div className="text-center py-12" data-testid="success-message">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-slate-900 mb-4">
                      Message Received!
                    </h3>
                    <p className="text-slate-600 mb-6">
                      Thanks for reaching out. I'll get back to you within 24 hours. 
                      If you need immediate assistance, feel free to call.
                    </p>
                    <Button 
                      onClick={() => setIsSubmitted(false)}
                      variant="outline"
                      className="border-2 border-slate-200 hover:border-slate-900 rounded-full px-6 py-2"
                      data-testid="send-another-btn"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6 relative" data-testid="contact-form">
                    <div>
                      <h3 className="font-['Playfair_Display'] text-xl font-semibold text-slate-900 mb-2">
                        Send a Message
                      </h3>
                      <p className="text-sm text-slate-500">
                        Fill out the form below and I'll get back to you soon.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-slate-700">Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Your name"
                          className="bg-white border-slate-200 focus:border-slate-900 h-12"
                          data-testid="input-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-700">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your@email.com"
                          className="bg-white border-slate-200 focus:border-slate-900 h-12"
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-slate-700">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(215) 416-6104"
                          className="bg-white border-slate-200 focus:border-slate-900 h-12"
                          data-testid="input-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-700">I am a...</Label>
                        <Select onValueChange={handleSelectChange} value={formData.clientType}>
                          <SelectTrigger 
                            className="bg-white border-slate-200 focus:border-slate-900 h-12"
                            data-testid="select-client-type"
                          >
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="agent">Real Estate Agent</SelectItem>
                            <SelectItem value="attorney">Attorney</SelectItem>
                            <SelectItem value="lender">Lender</SelectItem>
                            <SelectItem value="buyer">Home Buyer</SelectItem>
                            <SelectItem value="seller">Home Seller</SelectItem>
                            <SelectItem value="investor">Investor/Builder</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-slate-700">Subject</Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="What can I help you with?"
                        className="bg-white border-slate-200 focus:border-slate-900 h-12"
                        data-testid="input-subject"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-slate-700">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Tell me about your transaction or question..."
                        className="bg-white border-slate-200 focus:border-slate-900 min-h-[120px] resize-none"
                        data-testid="input-message"
                      />
                    </div>

                    {error && (
                      <p className="text-red-600 text-sm" data-testid="error-message">{error}</p>
                    )}

                    <Button 
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full py-4 font-medium transition-all relative z-10"
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-slate-500 text-center">
                      Your information is private and will only be used to respond to your inquiry.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 md:py-20 bg-slate-900 text-white" data-testid="contact-bottom-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-amber-500 font-medium mb-4">Prefer to Talk?</p>
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-semibold mb-4">
            I Actually Answer the Phone
          </h2>
          <p className="text-slate-300 mb-6">
            Sometimes it's just easier to talk. Give me a call and let's discuss your transaction.
          </p>
          <a href="tel:+12154166104" data-testid="call-btn">
            <Button className="bg-amber-700 text-white hover:bg-amber-600 rounded-full px-8 py-4 font-medium transition-all hover:scale-105">
              <Phone className="w-5 h-5 mr-2" />
              Call (215) 416-6104
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};
